/**
 * 🪶 **3D 파일(.glb)을 브라우저에서 가볍게** — `scripts/slim-glb.py` 와 같은 일을 관리자 브라우저가 한다(2026-09-14).
 *
 * ## 왜
 * 운영자가 올린 원본이 **그대로 아이 폰에 간다.** 국가유산청 수막새 원본은 37MB 인데 그중 **99% 가 그림(텍스처) 세 장**이고
 * 모양은 2,224점뿐이다. 명령줄 도구만 있으면 운영자는 못 쓴다(「게시에는 단추가 필요하다」) — 그래서 칸에 단추로 붙인다.
 *
 * ## 무엇을 하나 (의존성 없이)
 * ① 그림을 다시 굽는다 — 바탕색은 1024px, 나머지(거칠기·법선·가림)는 512px. 굽는 손은 밖에서 받는다(`ImageEncoder`,
 *    브라우저는 캔버스 — `slimGlbCanvas.ts`). 다시 구운 것이 **더 크면 원래 것을 둔다.**
 * ② 접선(TANGENT)을 뺀다 — three 는 법선 지도에 접선이 없으면 화면에서 셈한다(보이는 차이 없음).
 * ③ 색인 u32 → u16(점이 65,535 개 아래일 때).
 * ④ 속성마다 제 bufferView 로 풀어 담고(stride 없이) 4바이트 정렬로 다시 싼다.
 *
 * ## 🔴 규격대로 적는다
 * 스크립트는 그림을 WebP 로 바꾸며 `mimeType` 만 고쳤다(원본이 이미 WebP 확장을 쓴 파일이라 통했다). PNG 원본이면
 * **확장(`EXT_texture_webp`)을 선언하지 않은 WebP** 가 되어 검증기가 거절한다. 여기서는 텍스처를 확장으로 가리키고
 * `extensionsUsed/Required` 에 적는다. 캔버스가 WebP 를 못 만들면(사파리) JPEG·PNG 로 — 그땐 확장 없이 core 로 둔다.
 *
 * ## 🔴 못 줄이는 파일은 **왜 못 하는지** 말한다
 * 이미 압축된 것(Draco·meshopt)·밖에 있는 파일을 가리키는 것(`uri`)·흩어진(sparse) 속성·인스턴싱은 손대지 않는다.
 */

export class GlbSlimError extends Error {}

type Json = Record<string, unknown>;
interface BufferView {
  buffer: number;
  byteOffset?: number;
  byteLength: number;
  byteStride?: number;
  target?: number;
}
interface Accessor {
  bufferView?: number;
  byteOffset?: number;
  componentType: number;
  count: number;
  type: string;
  sparse?: unknown;
  [k: string]: unknown;
}
interface Img {
  bufferView?: number;
  mimeType?: string;
  uri?: string;
  [k: string]: unknown;
}
interface Tex {
  source?: number;
  extensions?: Record<string, { source?: number } | undefined>;
  [k: string]: unknown;
}
interface Prim {
  attributes: Record<string, number>;
  indices?: number;
  targets?: Record<string, number>[];
  extensions?: Json;
  [k: string]: unknown;
}
export interface GlbJson {
  bufferViews?: BufferView[];
  buffers?: { byteLength: number; uri?: string }[];
  accessors?: Accessor[];
  images?: Img[];
  textures?: Tex[];
  materials?: Json[];
  meshes?: { primitives: Prim[] }[];
  skins?: { inverseBindMatrices?: number }[];
  animations?: { samplers: { input: number; output: number }[] }[];
  extensionsUsed?: string[];
  extensionsRequired?: string[];
  [k: string]: unknown;
}

export type EncodedMime = "image/webp" | "image/jpeg" | "image/png";
export type ImageEncoder = (
  data: Uint8Array,
  mime: string,
  opts: { maxPx: number; quality: number },
) => Promise<{ data: Uint8Array; mime: EncodedMime; px: number }>;

export interface SlimReport {
  beforeKb: number;
  afterKb: number;
  droppedTangents: number;
  textures: { slots: string; px: number; beforeKb: number; afterKb: number; mime: string }[];
}

export interface SlimOpts {
  basePx: number;
  otherPx: number;
  quality: number;
  normalQuality: number;
}

const GLB_MAGIC = 0x46546c67;
const CHUNK_JSON = 0x4e4f534a;
const CHUNK_BIN = 0x004e4942;
const WEBP_EXT = "EXT_texture_webp";

/** GLB → JSON + BIN. 깨졌으면 운영자 말로 던진다. */
export function readGlb(buf: ArrayBuffer): { json: GlbJson; bin: Uint8Array } {
  const dv = new DataView(buf);
  if (buf.byteLength < 20 || dv.getUint32(0, true) !== GLB_MAGIC) throw new GlbSlimError(".glb 파일이 아니에요(파일 머리말이 달라요).");
  if (dv.getUint32(4, true) !== 2) throw new GlbSlimError("glTF 2.0 파일이 아니에요.");
  let off = 12;
  let json: GlbJson | null = null;
  let bin = new Uint8Array(0);
  while (off + 8 <= buf.byteLength) {
    const len = dv.getUint32(off, true);
    const type = dv.getUint32(off + 4, true);
    const start = off + 8;
    if (start + len > buf.byteLength) throw new GlbSlimError("파일이 중간에 잘려 있어요.");
    if (type === CHUNK_JSON) json = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, start, len))) as GlbJson;
    else if (type === CHUNK_BIN) bin = new Uint8Array(buf, start, len);
    off = start + len;
  }
  if (!json) throw new GlbSlimError("파일 안에 3D 설명(JSON)이 없어요.");
  return { json, bin };
}

/**
 * 새 bufferView 들을 4바이트 정렬로 잇고 GLB 로 싼다.
 * @param targets bufferView 마다 용도(34962 점 데이터 · 34963 색인) — 적어 두면 검증기 힌트가 사라지고 엔진이 헤매지 않는다
 */
export function writeGlb(json: GlbJson, chunks: Uint8Array[], targets: (number | undefined)[] = []): Uint8Array<ArrayBuffer> {
  const pad4 = (n: number) => (4 - (n % 4)) % 4;
  const views: BufferView[] = [];
  let binLen = 0;
  for (const c of chunks) {
    binLen += pad4(binLen);
    const target = targets[views.length];
    views.push({ buffer: 0, byteOffset: binLen, byteLength: c.byteLength, ...(target ? { target } : {}) });
    binLen += c.byteLength;
  }
  binLen += pad4(binLen);
  const bin = new Uint8Array(binLen);
  chunks.forEach((c, i) => bin.set(c, views[i]!.byteOffset!));
  json.bufferViews = views;
  json.buffers = [{ byteLength: binLen }];
  const raw = new TextEncoder().encode(JSON.stringify(json));
  const jsonLen = raw.byteLength + pad4(raw.byteLength);
  const out = new Uint8Array(12 + 8 + jsonLen + 8 + binLen);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, GLB_MAGIC, true);
  dv.setUint32(4, 2, true);
  dv.setUint32(8, out.byteLength, true);
  dv.setUint32(12, jsonLen, true);
  dv.setUint32(16, CHUNK_JSON, true);
  out.set(raw, 20);
  out.fill(0x20, 20 + raw.byteLength, 20 + jsonLen); // JSON 은 공백으로 채운다(규격)
  dv.setUint32(20 + jsonLen, binLen, true);
  dv.setUint32(24 + jsonLen, CHUNK_BIN, true);
  out.set(bin, 28 + jsonLen);
  return out;
}

const COMP_BYTES: Record<number, number> = { 5120: 1, 5121: 1, 5122: 2, 5123: 2, 5125: 4, 5126: 4 };
const TYPE_N: Record<string, number> = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT2: 4, MAT3: 9, MAT4: 16 };

function viewBytes(json: GlbJson, bin: Uint8Array, i: number): Uint8Array {
  const v = json.bufferViews?.[i];
  if (!v) throw new GlbSlimError("3D 설명이 없는 자리를 가리켜요(깨진 파일).");
  const o = v.byteOffset ?? 0;
  if (v.buffer !== 0 || o + v.byteLength > bin.byteLength) throw new GlbSlimError("3D 데이터가 파일 밖을 가리켜요(깨진 파일).");
  return bin.subarray(o, o + v.byteLength);
}

/** 이 파일을 줄일 수 있나 — 못 하면 이유를 던진다. */
function assertSlimmable(json: GlbJson): void {
  const exts = [...(json.extensionsUsed ?? []), ...(json.extensionsRequired ?? [])];
  if (exts.some((e) => /draco|meshopt/i.test(e))) throw new GlbSlimError("이미 압축된 3D 예요(Draco·meshopt) — [그대로 올리기] 를 써 주세요.");
  if (exts.some((e) => /gpu_instancing/i.test(e))) throw new GlbSlimError("여러 개를 겹쳐 그리는(인스턴싱) 3D 는 줄이지 않아요.");
  if ((json.buffers ?? []).some((b) => b.uri)) throw new GlbSlimError("밖의 파일(.bin)을 가리키는 3D 예요 — 하나로 묶인 .glb 로 내보내 주세요.");
  if ((json.images ?? []).some((im) => im.uri || im.bufferView === undefined)) throw new GlbSlimError("그림이 파일 밖에 있어요 — 그림까지 묶인 .glb 로 내보내 주세요.");
  if ((json.accessors ?? []).some((a) => a.sparse)) throw new GlbSlimError("흩어진(sparse) 속성이 있는 3D 는 줄이지 않아요.");
}

function textureImage(t: Tex | undefined): number | undefined {
  return t?.source ?? t?.extensions?.[WEBP_EXT]?.source;
}

export async function slimGlb(buf: ArrayBuffer, encode: ImageEncoder, opts: SlimOpts): Promise<{ glb: Uint8Array<ArrayBuffer>; report: SlimReport }> {
  const { json, bin } = readGlb(buf);
  assertSlimmable(json);
  const chunks: Uint8Array[] = [];
  const targets: (number | undefined)[] = [];
  const keep = (data: Uint8Array, target?: number) => {
    chunks.push(data);
    targets.push(target);
    return chunks.length - 1;
  };

  // ① 그림 — 어느 자리에 쓰이나 보고 크기·품질을 정한다
  const slotsOf = new Map<number, Set<string>>();
  const mark = (ref: unknown, slot: string) => {
    const idx = (ref as { index?: number } | undefined)?.index;
    if (typeof idx !== "number") return;
    const img = textureImage(json.textures?.[idx]);
    if (typeof img !== "number") return;
    if (!slotsOf.has(img)) slotsOf.set(img, new Set());
    slotsOf.get(img)!.add(slot);
  };
  for (const m of json.materials ?? []) {
    const pbr = (m.pbrMetallicRoughness ?? {}) as Json;
    mark(pbr.baseColorTexture, "바탕색");
    mark(pbr.metallicRoughnessTexture, "거칠기");
    mark(m.normalTexture, "법선");
    mark(m.occlusionTexture, "가림");
    mark(m.emissiveTexture, "빛");
  }
  const report: SlimReport = { beforeKb: buf.byteLength / 1024, afterKb: 0, droppedTangents: 0, textures: [] };
  const images = json.images ?? [];
  for (let i = 0; i < images.length; i++) {
    const img = images[i]!;
    const old = viewBytes(json, bin, img.bufferView!);
    const slots = slotsOf.get(i) ?? new Set<string>();
    const sharp = slots.has("바탕색") || slots.has("빛");
    const enc = await encode(old, img.mimeType ?? "image/png", {
      maxPx: sharp ? opts.basePx : opts.otherPx,
      quality: slots.has("법선") ? opts.normalQuality : opts.quality,
    });
    const better = enc.data.byteLength < old.byteLength;
    img.bufferView = keep(better ? enc.data : old.slice());
    if (better) img.mimeType = enc.mime;
    report.textures.push({
      slots: [...slots].join("/") || "?",
      px: enc.px,
      beforeKb: old.byteLength / 1024,
      afterKb: (better ? enc.data.byteLength : old.byteLength) / 1024,
      mime: img.mimeType ?? "",
    });
  }
  // 텍스처가 가리키는 방식을 그림 형식에 맞춘다 — WebP 는 확장으로, 나머지는 core 로
  for (const t of json.textures ?? []) {
    const src = textureImage(t);
    if (typeof src !== "number") continue;
    const isWebp = images[src]?.mimeType === "image/webp";
    const ext = { ...(t.extensions ?? {}) };
    if (isWebp) {
      ext[WEBP_EXT] = { source: src };
      delete t.source;
    } else {
      delete ext[WEBP_EXT];
      t.source = src;
    }
    if (Object.keys(ext).length) t.extensions = ext;
    else delete t.extensions;
  }
  const anyWebp = images.some((im) => im.mimeType === "image/webp");
  const setExt = (list: string[] | undefined) => {
    const s = new Set((list ?? []).filter((e) => e !== WEBP_EXT));
    if (anyWebp) s.add(WEBP_EXT);
    return s.size ? [...s] : undefined;
  };
  json.extensionsUsed = setExt(json.extensionsUsed);
  json.extensionsRequired = setExt(json.extensionsRequired);
  if (!json.extensionsUsed) delete json.extensionsUsed;
  if (!json.extensionsRequired) delete json.extensionsRequired;

  // ② 접선 빼기 — 쓰는 accessor 를 모은다(모양·색인·모핑·뼈·애니까지. 빠뜨리면 그 데이터가 사라진다)
  const used = new Set<number>();
  const ARRAY_BUFFER = 34962; // 점 데이터(모양·법선·UV)
  const ELEMENT_ARRAY_BUFFER = 34963; // 색인
  const vertexAcc = new Set<number>();
  const indexAcc = new Set<number>();
  for (const mesh of json.meshes ?? []) {
    for (const prim of mesh.primitives) {
      if ("TANGENT" in prim.attributes) {
        delete prim.attributes.TANGENT;
        report.droppedTangents++;
      }
      Object.values(prim.attributes).forEach((a) => {
        used.add(a);
        vertexAcc.add(a);
      });
      if (typeof prim.indices === "number") {
        used.add(prim.indices);
        indexAcc.add(prim.indices);
      }
      for (const tg of prim.targets ?? []) Object.values(tg).forEach((a) => used.add(a));
    }
  }
  for (const s of json.skins ?? []) if (typeof s.inverseBindMatrices === "number") used.add(s.inverseBindMatrices);
  for (const an of json.animations ?? []) for (const sm of an.samplers) {
    used.add(sm.input);
    used.add(sm.output);
  }

  // ③ 색인 u16 · ④ 속성마다 제 bufferView 로 풀어 담는다
  const accessors = json.accessors ?? [];
  accessors.forEach((acc, ai) => {
    if (acc.bufferView === undefined) return;
    if (!used.has(ai)) {
      delete acc.bufferView; // 뺀 접선 — 둘 다 지운다(byteOffset 만 남으면 검증기가 거절한다)
      delete acc.byteOffset;
      return;
    }
    const raw = viewBytes(json, bin, acc.bufferView);
    const stride = json.bufferViews![acc.bufferView]!.byteStride;
    const off = acc.byteOffset ?? 0;
    const comp = COMP_BYTES[acc.componentType];
    const n = TYPE_N[acc.type];
    if (!comp || !n) throw new GlbSlimError("모르는 데이터 형식이 있어요(깨진 파일).");
    const elem = comp * n;
    const step = stride ?? elem;
    if (off + (acc.count - 1) * step + elem > raw.byteLength) throw new GlbSlimError("3D 데이터가 제 자리보다 길어요(깨진 파일).");
    if (acc.componentType === 5125 && acc.type === "SCALAR" && !stride) {
      const dv = new DataView(raw.buffer, raw.byteOffset + off, acc.count * 4);
      let max = 0;
      for (let k = 0; k < acc.count; k++) max = Math.max(max, dv.getUint32(k * 4, true));
      if (max < 65535) {
        const out = new Uint8Array(acc.count * 2);
        const ov = new DataView(out.buffer);
        for (let k = 0; k < acc.count; k++) ov.setUint16(k * 2, dv.getUint32(k * 4, true), true);
        acc.componentType = 5123;
        acc.byteOffset = 0;
        acc.bufferView = keep(out, ELEMENT_ARRAY_BUFFER);
        return;
      }
    }
    const out = new Uint8Array(acc.count * elem);
    for (let k = 0; k < acc.count; k++) out.set(raw.subarray(off + k * step, off + k * step + elem), k * elem);
    acc.byteOffset = 0;
    acc.bufferView = keep(out, indexAcc.has(ai) ? ELEMENT_ARRAY_BUFFER : vertexAcc.has(ai) ? ARRAY_BUFFER : undefined);
  });

  const glb = writeGlb(json, chunks, targets);
  report.afterKb = glb.byteLength / 1024;
  return { glb, report };
}
